import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /**
   * Incremented on every playMedia/pauseMedia so in-flight play() promises
   * cannot revive isPlaying after the user (or gallery) has paused.
   */
  #playbackSession = 0;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#pauseFromExternalPlayback, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.#pauseFromExternalPlayback, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  // Stable listener reference — `.bind(this)` in add/remove pairs does not match.
  #pauseFromExternalPlayback = () => {
    this.pauseMedia();
  };

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.removeAttribute('data-user-paused');
    this.loadContent(true);
    // Prepared videos strip autoplay before insertion; start them explicitly.
    // Already-playing autoplay videos no-op safely inside play().
    this.playMedia();
  };

  /**
   * Speculatively prepares Shopify-hosted video without playing it.
   * External embeds are not eligible.
   * @returns {boolean} Whether preparation happened.
   */
  prepareContent() {
    if (this.getAttribute('data-media-loaded') || this.getAttribute('data-media-prepared')) return false;

    const content = this.#cloneTemplateContent();
    if (!(content instanceof HTMLVideoElement)) return false;

    content.removeAttribute('autoplay');
    content.setAttribute('preload', 'auto');
    this.appendChild(content);
    this.setAttribute('data-media-prepared', 'true');
    this.#attachVideoLifecycle(content);

    return true;
  }

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const wasPrepared = this.hasAttribute('data-media-prepared');
    /** @type {Element | null} */
    let content = wasPrepared ? this.querySelector('video') : this.#cloneTemplateContent();

    if (!content) return;

    // Pause siblings whether this is a fresh clone or a prepared activation.
    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    if (!wasPrepared) this.appendChild(content);

    this.setAttribute('data-media-loaded', 'true');

    if (content instanceof HTMLVideoElement) {
      this.#applyAdaptiveLoop(content);
      this.#attachVideoLifecycle(content);
    } else {
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    }

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }
  }

  /**
   * @returns {Element | null}
   */
  #cloneTemplateContent() {
    return this.querySelector('template')?.content.firstElementChild?.cloneNode(true) ?? null;
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      // Remember intentional pause so gallery autoplay does not immediately resume.
      this.setAttribute('data-user-paused', '');
      this.pauseMedia();
    } else {
      this.removeAttribute('data-user-paused');
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
      this.#playbackSession += 1;
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
      return undefined;
    }

    const video = this.querySelector('video');
    if (!video) return undefined;

    const session = ++this.#playbackSession;
    this.#attachVideoLifecycle(video);
    // Optimistic UI (Horizon behavior): don't wait for play() — HLS/aborts
    // otherwise leave isPlaying false while the user already clicked play.
    this.isPlaying = true;
    this.updatePlayPauseHint(true);
    const pendingPlay = video.play();
    this.#trackVideoPlay(pendingPlay, session);

    return pendingPlay;
  }

  /**
   * Applies the gallery loop setting to adaptive-streaming videos (rendered
   * without the loop attribute so Shopify includes the HLS source).
   * @param {HTMLVideoElement} video
   */
  #applyAdaptiveLoop(video) {
    const gallery = this.closest('media-gallery');
    if (gallery?.dataset.adaptiveVideo !== 'true') return;

    video.loop = gallery.dataset.videoLoop !== 'false';
  }

  /**
   * Reveals the video on first playing event; restores the poster on error.
   * @param {HTMLVideoElement} video
   */
  #attachVideoLifecycle(video) {
    if (!(video instanceof HTMLVideoElement) || video.dataset.lifecycleAttached) return;

    video.dataset.lifecycleAttached = 'true';
    video.addEventListener('playing', () => {
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
    });
    video.addEventListener('error', () => {
      this.#restorePoster();
    });
  }

  /**
   * Marks playback state once play() resolves; restores the poster on rejection.
   * @param {Promise<void> | undefined} pendingPlay
   * @param {number} session - Playback session from playMedia; ignored if stale.
   */
  async #trackVideoPlay(pendingPlay, session) {
    if (!pendingPlay || typeof pendingPlay.then !== 'function') {
      if (session !== this.#playbackSession) return;
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      return;
    }

    try {
      await pendingPlay;
      const video = this.querySelector('video');
      const stale = session !== this.#playbackSession;
      // Ignore resolutions after pauseMedia/playMedia superseded this attempt.
      if (stale) return;
      // play() can resolve after a pause in some engines — sync UI back down.
      if (!video || video.paused) {
        this.isPlaying = false;
        if (this.getAttribute('data-media-loaded')) this.updatePlayPauseHint(false);
        return;
      }
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
    } catch (error) {
      if (session !== this.#playbackSession) return;
      if (error instanceof DOMException && error.name === 'AbortError') {
        // Interrupted without a newer session (rare) — match real video state.
        const video = this.querySelector('video');
        if (!video || video.paused) {
          this.isPlaying = false;
          if (this.getAttribute('data-media-loaded')) this.updatePlayPauseHint(false);
        }
        return;
      }
      this.#restorePoster();
    }
  }

  /**
   * Restores a usable poster and play button after a playback failure.
   */
  #restorePoster() {
    this.refs.deferredMediaPlayButton?.classList.remove('deferred-media__playing');
    this.isPlaying = false;
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    // Invalidate in-flight play() trackers before pausing so they cannot
    // flip isPlaying back to true after this pause.
    this.#playbackSession += 1;

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
